/**
 * BarCompare — token-themed comparison bar chart (recharts wrapper).
 * Grouped or stacked series over a shared category axis. Supports horizontal
 * layout (good for ranked lists / RTL-friendly category labels).
 */
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from 'recharts';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { CHART_COLORS, AXIS_TICK, seriesColor, isDocRtl } from './chartTheme';
import { ChartTooltip } from './ChartTooltip';

export interface BarCompareSeries {
  key: string;
  name?: string;
  color?: string;
}

export interface BarCompareProps {
  data: Array<Record<string, number | string>>;
  /** Category axis key. */
  xKey: string;
  series: BarCompareSeries[];
  /** Stack series instead of grouping. Default false. */
  stacked?: boolean;
  /** Horizontal bars (category on Y). Default false. */
  horizontal?: boolean;
  /** When a single series, colour each bar from the palette by index. Default false. */
  colorByPoint?: boolean;
  height?: number;
  valueFormatter?: (value: number | string | undefined) => string;
  showGrid?: boolean;
  showLegend?: boolean;
  /** Required: short text description for screen readers. */
  ariaLabel: string;
  className?: string;
}

export function BarCompare({
  data,
  xKey,
  series,
  stacked = false,
  horizontal = false,
  colorByPoint = false,
  height = 240,
  valueFormatter,
  showGrid = true,
  showLegend = false,
  ariaLabel,
  className,
}: BarCompareProps) {
  const reduced = usePrefersReducedMotion();
  const rtl = isDocRtl();
  const radius: [number, number, number, number] = horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0];

  return (
    <figure role="img" aria-label={ariaLabel} className={className} style={{ margin: 0, width: '100%' }}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
        >
          {showGrid && (
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} vertical={horizontal} horizontal={!horizontal} />
          )}
          {horizontal ? (
            <>
              <XAxis type="number" reversed={rtl} tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={valueFormatter} />
              <YAxis type="category" dataKey={xKey} orientation={rtl ? 'right' : 'left'} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: CHART_COLORS.grid }} width={96} />
            </>
          ) : (
            <>
              <XAxis dataKey={xKey} reversed={rtl} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: CHART_COLORS.grid }} />
              <YAxis orientation={rtl ? 'right' : 'left'} tick={AXIS_TICK} tickLine={false} axisLine={false} width={48} tickFormatter={valueFormatter} />
            </>
          )}
          <Tooltip cursor={{ fill: 'rgba(147, 51, 234, 0.06)' }} content={<ChartTooltip valueFormatter={valueFormatter} />} />
          {showLegend && <Legend wrapperStyle={{ fontSize: 12, color: CHART_COLORS.text }} />}
          {series.map((s, i) => {
            const color = s.color ?? seriesColor(i);
            return (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name ?? s.key}
                fill={color}
                radius={radius}
                stackId={stacked ? 'stack' : undefined}
                isAnimationActive={!reduced}
                maxBarSize={48}
              >
                {colorByPoint && series.length === 1
                  ? data.map((_, idx) => <Cell key={idx} fill={seriesColor(idx)} />)
                  : null}
              </Bar>
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </figure>
  );
}
